import { AutocompleteOption, ControlledAutocomplete, ControlledAutocompleteVariant } from "../../../shared/components/ControlledAutocomplete";
import { Box, Button, Stack } from "@mui/material";
import { AutoComplete, FieldControl } from "../../../shared/components";
import { useUpdateAsset } from "../hooks/useUpdateAsset";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useAssetDetails } from "../hooks/useAssetDetails";
import { useStageGate } from "../hooks/useStageGate";
import { clientTheme } from "../../../theme/clientTheme";

const hiddenProjectFormFields = clientTheme.layout?.hiddenProjectFormFields ?? [];

export interface AssetProps {
    asset_id: number;
    category: string;
    category_id: string;
    narms_value: string;
    asset_category: string;
    asset_type: string;
    iln_id: null;
    iln: number;
    functional_location: string;
    sap_hierarchy: string;
    asset_budget: string;
    id: number;
    asset_description: string;
    programme_id: number | null;
    programme_name: string | null;
};

export const Asset = ({ asset: initialAsset, onSuccess, onClose, projectId, isEdit }: { asset?: AssetProps; onSuccess: () => void; onClose: () => void; projectId: string; isEdit: boolean }) => {

    const { getFilterOptions } = useDisplayField([]);
    const { assetData } = useAssetDetails(projectId);

    const { asset, assetErrors, handleAssetChange, handleSubmit, submitting } = useUpdateAsset({ initialAsset, projectId, onSuccess, isEdit, existingAssets: assetData });

    const { stageGateData } = useStageGate(projectId);

    const isAssetEditable = isEdit && stageGateData?.find(
        (stageGate: { description: string; status: string }) => stageGate.description === "Estimate/Validation"
    )?.status === "completed";

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2, p: 2 }}>

                <ControlledAutocomplete
                    type={"PROGRAMME"}
                    value={asset.programme}
                    label="Programme"
                    onSelect={(value: AutocompleteOption | AutocompleteOption[] | null, type: ControlledAutocompleteVariant) => {
                        handleAssetChange("programme", value);
                    }}
                    error={!!(assetErrors?.programme)}
                    helperText={assetErrors?.programme}
                    disabled={isAssetEditable}
                    required
                />

                <FieldControl
                    keyName="asset_category"
                    label='Asset Category'
                    variant="select"
                    value={asset.asset_category}
                    options={getFilterOptions("ASSET_CATEGORY").map((option) => ({ label: option.display_text, value: option.key }))}
                    onChange={(_key: string, value: any) => handleAssetChange("asset_category", value)}
                    error={!!(assetErrors?.asset_category)}
                    helperText={assetErrors?.asset_category}
                    disabled={isAssetEditable}
                    required
                />

                {
                    (asset.programme && asset.programme.id == 6) && (
                        <FieldControl
                            keyName="narms_value"
                            label='NARMS Value'
                            variant="input"
                            value={asset.narms_value}
                            onChange={(_key: string, value: any) => handleAssetChange("narms_value", value)}
                            error={!!(assetErrors?.narms_value)}
                            helperText={assetErrors?.narms_value}
                            disabled={isAssetEditable}
                            required
                        />
                    )
                }
                <AutoComplete
                    value={asset.iln_number_id}
                    onSelect={(value: any) => handleAssetChange("iln_number_id", value)}
                    name="iln"
                    label="HCode"
                    disabled={isAssetEditable}
                    error={!!(assetErrors?.iln_number_id)}
                    helperText={assetErrors?.iln_number_id}
                    required

                />
              <FieldControl
                    keyName="functional_location"
                    label="Functional Location"
                    variant="input"
                    value={asset.functional_location}
                    onChange={(_key: string, value: any) => handleAssetChange("functional_location", value)}
                    error={!!(assetErrors?.functional_location)}
                    helperText={assetErrors?.functional_location}
                    required
                />

                <FieldControl
                    keyName="sap_hierarchy"
                    label="SAP Hierarchy"
                    variant="input"
                    value={asset.sap_hierarchy}
                    onChange={(_key: string, value: any) => handleAssetChange("sap_hierarchy", value)}
                    error={!!(assetErrors?.sap_hierarchy)}
                    helperText={assetErrors?.sap_hierarchy}
                    required
                />

                <FieldControl
                    keyName="asset_budget"
                    label="Asset Budget"
                    variant="input"
                    value={asset.asset_budget}
                    onChange={(_key: string, value: any) => handleAssetChange("asset_budget", value)}
                    error={!!(assetErrors?.asset_budget)}
                    helperText={assetErrors?.asset_budget}
                    disabled={isAssetEditable}
                    required
                />

                <FieldControl
                    keyName="asset_type"
                    label='Asset Type'
                    variant="select"
                    value={asset.asset_type}
                    options={getFilterOptions("ASSET_TYPE").filter((a) => { return asset.asset_category === a.parent_key }).map((option) => ({ label: option.display_text, value: option.key }))}
                    onChange={(_key: string, value: any) => handleAssetChange("asset_type", value)}
                    error={!!(assetErrors?.asset_type)}
                    helperText={assetErrors?.asset_type}
                    disabled={isAssetEditable}
                    required
                />

                <FieldControl
                    keyName="asset_description"
                    label="Asset"
                    variant="input"
                    value={asset.asset_description}
                    onChange={(_key: string, value: any) => handleAssetChange("asset_description", value)}
                    error={!!(assetErrors?.asset_description)}
                    helperText={assetErrors?.asset_description}
                    disabled={isAssetEditable}
                    required
                />

            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end" flexShrink={0} bgcolor={'action.hover'} p={2}>
                <Button variant="outlined" onClick={onClose} disabled={submitting}>Close</Button>
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={submitting}>Save</Button>
            </Stack>

        </Box>
    );
};